import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Configuration options for the useUnsavedChanges hook.
 */
interface UseUnsavedChangesOptions {
	/** Whether there are unsaved changes that need confirmation before leaving */
	isDirty: boolean;
	/** Custom confirmation message (default: "You have unsaved changes...") */
	message?: string;
}

/**
 * Prevents navigation away from the page when there are unsaved changes.
 *
 * This hook provides a safety net for users by showing confirmation dialogs before
 * they accidentally navigate away from a page with unsaved work. It handles both
 * in-app navigation (React Router) and browser-level navigation (page close, refresh).
 *
 * Key behaviors:
 * - Blocks React Router navigation with window.confirm() dialog
 * - Blocks browser navigation with beforeunload event
 * - Only activates when isDirty is true
 * - Uses refs to avoid stale closure issues
 * - Cleans up event listeners and blocks on unmount
 *
 * Navigation types handled:
 * - In-app routing: history.push(), history.replace(), back button
 * - Browser navigation: page refresh, tab close, address bar navigation
 * - External navigation: clicking external links
 *
 * Implementation notes:
 * - Uses refs for isDirty and message to ensure latest values in callbacks
 * - Uses isBlockingRef to prevent infinite confirmation loops
 * - Unblocks navigation after user confirms to allow the navigation to proceed
 * - beforeunload event.returnValue is set for cross-browser compatibility
 *
 * Typical usage with auto-save:
 * - Pair this with useAutoSave's isDirty flag
 * - Shows confirmation only when save is pending (not yet sent to server)
 * - Once save completes, isDirty becomes false and navigation is allowed
 *
 * @param options - Configuration object
 * @param options.isDirty - Whether there are unsaved changes
 * @param options.message - Custom confirmation message
 *
 * @example
 * ```typescript
 * const { isDirty, saveStep } = useAutoSave({ templateId });
 *
 * useUnsavedChanges({
 *   isDirty,
 *   message: 'Your automation changes have not been saved. Leave anyway?'
 * });
 *
 * // User tries to navigate away:
 * // - If isDirty=true: Shows confirmation dialog
 * // - If user confirms: Navigation proceeds
 * // - If user cancels: Stays on current page
 * ```
 *
 * @example
 * ```typescript
 * // Conditional blocking based on form state:
 * const [formData, setFormData] = useState(initialData);
 * const hasChanges = !isEqual(formData, initialData);
 *
 * useUnsavedChanges({
 *   isDirty: hasChanges && !isSaving
 * });
 * ```
 */
export function useUnsavedChanges({
	isDirty,
	message = 'You have unsaved changes. Are you sure you want to leave?',
}: UseUnsavedChangesOptions) {
	const history = useHistory();
	const isBlockingRef = useRef(false);
	// Use refs to capture latest values and avoid stale closures
	const isDirtyRef = useRef(isDirty);
	const messageRef = useRef(message);

	// Keep refs in sync with props
	useEffect(() => {
		isDirtyRef.current = isDirty;
		messageRef.current = message;
	}, [isDirty, message]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			// Use ref to get latest value
			if (isDirtyRef.current) {
				event.preventDefault();
				event.returnValue = messageRef.current;
				return messageRef.current;
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		const unblock = history.block((location, action) => {
			if (isBlockingRef.current) {
				return;
			}

			// Use ref to get latest value
			if (isDirtyRef.current) {
				const shouldLeave = window.confirm(messageRef.current);
				if (shouldLeave) {
					isBlockingRef.current = true;
					unblock();
					if (action === 'PUSH') {
						history.push(location.pathname);
					} else if (action === 'REPLACE') {
						history.replace(location.pathname);
					} else if (action === 'POP') {
						history.goBack();
					}
				}
				return false;
			}
		});

		return () => {
			unblock();
		};
	}, [history]);
}
